import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog } from '@robocorp/components';
import { styled } from '@robocorp/theme';

import robomonkeyAstronout from '~/assets/mark/astronaut.png';
import { useAuth } from '~/auth/AuthProvider';
import { useLogout } from '~/hooks';
import { Header } from './old-ds-components';

const StyledImage = styled('img')`
  position: absolute;
  max-width: 120px;
  top: 50%;
  transform: translate(20px, -50%);

  display: none;
  @media (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    display: block;
  }
`;

const StyledHeader = styled(Header)`
  margin: 0;
  @media (min-width: ${({ theme }) => theme.breakpoints[0]}) {
    margin: 0 0 0 150px;
  }

  > div {
    max-width: 100%;
  }
`;

interface Props {
  getTokenExpiration: () => number | undefined;
  onLogout: () => void;
  children: ReactNode;
}

const AuthSessionGuard: FC<Props> = ({ getTokenExpiration, onLogout, children }) => {
  const { t } = useTranslation();

  const [isExpired, setIsExpired] = useState<boolean>(false);
  const reAuthenticate = () => document.location.reload();

  useEffect(() => {
    let timeout: number;
    const tokenExpirationCheck = () => {
      const tokenExpiration = getTokenExpiration();
      if (tokenExpiration === undefined) {
        return;
      }

      const tokenTimeLeft = Math.max(tokenExpiration * 1000 - Date.now(), 0);
      setIsExpired(tokenTimeLeft <= 0);

      /**
       * Re-check token once more when it's expected to be expired,
       * so there wouldn't be false positives before interrupting user action.
       */
      if (tokenTimeLeft > 0) {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(tokenExpirationCheck, Math.max(tokenTimeLeft, 3000));
      }
    };

    tokenExpirationCheck();
    return () => {
      window.clearTimeout(timeout);
    };
  }, [getTokenExpiration]);

  return (
    <>
      <Dialog open={isExpired} onClose={onLogout} width={650}>
        <Dialog.Content>
          <StyledImage src={robomonkeyAstronout} alt={t('sessionGuardMessage')} />

          <Box display="flex" flexDirection="column" justifyContent="center" px={20} pt={40}>
            <Box display="flex" alignItems="center">
              <StyledHeader
                heading={t('sessionGuardMessage')}
                subheading={t('sessionGuardDescription')}
              />
            </Box>

            <Box alignSelf="center" flex={1} pt={40} pb={20}>
              <Button onClick={reAuthenticate}>{t('sessionGuardAction')}</Button>
            </Box>
          </Box>
        </Dialog.Content>
      </Dialog>
      {!isExpired && children}
    </>
  );
};

/**
 * If allowRefreshTokensWithRotation is explicitly set to `false` will wrap
 * child content in session guard that will unmount children when auth token is expired
 * to prevent failing requests or ws heartbeats going on in background
 */
const AuthSessionGuardWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const { client: clientWrapper } = useAuth();
  const client = (clientWrapper as any).client ?? {};
  const onLogout = useLogout();

  const getTokenExpiration = useCallback(() => {
    const { expiresIn, issuedAt } = client.tokenResult ?? {};
    if (typeof issuedAt !== 'number' || typeof expiresIn !== 'number') {
      return undefined;
    }
    return issuedAt + expiresIn;
  }, []);

  const isTokenRotationDisabled = client.config?.allowRefreshTokensWithRotation === false;
  if (isTokenRotationDisabled) {
    return (
      <AuthSessionGuard getTokenExpiration={getTokenExpiration} onLogout={onLogout}>
        {children}
      </AuthSessionGuard>
    );
  }

  return <>{children}</>;
};

export default AuthSessionGuardWrapper;
